/**
 * 有料プランの定義。料金仕様書 v1.0 の 4段 (無料枠 / 月額 / 半年 / 年間) のうち、
 * 決済が発生する 3 つをここで一元管理する。
 *
 * ## plan_type について
 *
 * 3 プランはいずれも「全コースが使える」で機能差が無く、違うのは支払い周期だけ。
 * なので DB の plan_type は既存の最上位 "tsukuru" のまま据え置き、
 * billing_period で月額 / 半年 / 年間を区別する。
 * plan_type を増やすと PLAN_HIERARCHY (free:0 / hajimeru:1 / tsukuru:2 / nobasu:3) や
 * レッスンの解放判定を api-go 側まで含めて全部触ることになり、
 * 得られるものが「名前が変わる」だけなので割に合わない。
 *
 * ## price ID をハードコードしない理由
 *
 * テスト / 本番でそれぞれ別の price ID になるため、以前は両方をコードに
 * 直接書いて分岐していた。price を作り直すたびにコード修正が必要になるので、
 * Stripe 側の lookup_key で引く方式にした。テストと本番で同じ lookup_key を
 * 付けておけば、コードは環境を意識しなくてよくなる。
 */

/** URL の /code/pricing/:plan に出てくる識別子 */
export type PaidPlanId = "monthly" | "half" | "annual";

export type PaidPlan = {
  id: PaidPlanId;
  /** 表示名 */
  name: string;
  /** 税込の請求額 */
  amount: number;
  /** Stripe の price に付けた lookup_key */
  lookupKey: string;
  /** DB の subscriptions.plan_type */
  planType: "tsukuru";
  /** DB の subscriptions.billing_period */
  billingPeriod: "monthly" | "half_year" | "yearly";
  /** 「1ヶ月ごと」など、最終確認画面に出す更新周期 */
  intervalLabel: string;
  /**
   * Stripe の price に期待する更新間隔。
   * lookup_key を付け間違えたときに、表示と違う金額で課金してしまうのを
   * 決済の直前に検出するために持つ
   */
  recurring: { interval: "month" | "year"; intervalCount: number };
  /**
   * 月あたり換算の注記。月額プランには無い。
   *
   * 「◯%オフ」は月額 6,980 円を基準に実際に安くなるときだけ書く。
   * 月額を基準に実際に安くならない金額を付けたら、割引の文言は書かない
   */
  perMonthLabel?: string;
  /**
   * 無料トライアルの日数。
   *
   * 3 プランとも 30 日。トライアル明けに請求されるのは「そのプランの金額」で、
   * 月額へ寄せ替えたりはしない。半年を選んだ人は 30 日後に 34,800 円、
   * 年間なら 54,800 円が一度に落ちる。金額が大きいぶん、申込画面と決済画面で
   * 初回請求日と請求額を必ず出す。
   */
  trialDays?: number;
};

export const PAID_PLANS: Record<PaidPlanId, PaidPlan> = {
  monthly: {
    id: "monthly",
    name: "月額プラン",
    amount: 6980,
    lookupKey: "yumesaku_monthly",
    planType: "tsukuru",
    billingPeriod: "monthly",
    intervalLabel: "1ヶ月ごと",
    recurring: { interval: "month", intervalCount: 1 },
    trialDays: 30,
  },
  half: {
    id: "half",
    name: "半年プラン",
    amount: 34800,
    lookupKey: "yumesaku_half",
    planType: "tsukuru",
    billingPeriod: "half_year",
    intervalLabel: "6ヶ月ごと",
    recurring: { interval: "month", intervalCount: 6 },
    perMonthLabel: "月あたり 5,800円・約17%オフ",
    trialDays: 30,
  },
  annual: {
    id: "annual",
    name: "年間プラン",
    amount: 54800,
    lookupKey: "yumesaku_annual",
    planType: "tsukuru",
    billingPeriod: "yearly",
    intervalLabel: "1年ごと",
    recurring: { interval: "year", intervalCount: 1 },
    perMonthLabel: "月あたり 約4,567円・約35%オフ",
    trialDays: 30,
  },
};

export function isPaidPlanId(value: string | undefined): value is PaidPlanId {
  return value === "monthly" || value === "half" || value === "annual";
}

/**
 * 旧 URL の受け皿。/code/pricing/tsukuru などで貼られたリンクが死なないようにする
 */
export const LEGACY_PLAN_ALIASES: Record<string, PaidPlanId> = {
  tsukuru: "monthly",
  hajimeru: "monthly",
};

/** 円表記。金額は必ずこれを通して出す */
export function formatYen(amount: number): string {
  return `${amount.toLocaleString("ja-JP")}円`;
}

/**
 * 課金日・トライアル終了日の表記。
 *
 * getFullYear() などをそのまま使うと Workers 上では UTC の日付になり、
 * 日本時間の 0 時〜9 時に見た人にだけ 1 日前の日付が出る。
 * 請求日が 1 日ズレて見えるのは特商法の「支払時期」として困るので、
 * タイムゾーンを明示して組み立てる。
 */
export function formatJaBillingDate(date: Date): string {
  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}年${get("month")}月${get("day")}日`;
}
